import type React from 'react';
import { useCallback } from 'react';

import { ActiveFiltersContainer, ClearAllButton, FilterChip, FiltersLabel } from './styled';
import { type ElectionFilters } from './useElectionFilters';

interface ActiveFilter {
	key: keyof ElectionFilters;
	label: string;
	value?: string;
}

interface ActiveFiltersProps {
	filters: ActiveFilter[];
	onRemoveFilter: (key: keyof ElectionFilters, value?: string) => void;
	onClearAll: () => void;
}

const CloseIcon = () => (
	<svg
		width='10'
		height='10'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='3'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<line
			x1='18'
			y1='6'
			x2='6'
			y2='18'
		/>
		<line
			x1='6'
			y1='6'
			x2='18'
			y2='18'
		/>
	</svg>
);

export const ActiveFilters: React.FC<ActiveFiltersProps> = ({ filters, onRemoveFilter, onClearAll }) => {
	const handleRemoveFilter = useCallback(
		(key: keyof ElectionFilters, value?: string) => () => {
			onRemoveFilter(key, value);
		},
		[onRemoveFilter]
	);

	if (filters.length === 0) {
		return null;
	}

	return (
		<ActiveFiltersContainer
			role='region'
			aria-label='Active filters'
		>
			<FiltersLabel>Active:</FiltersLabel>
			{filters.map((filter, index) => (
				<FilterChip
					key={`${filter.key}-${filter.value || index}`}
					type='button'
					onClick={handleRemoveFilter(filter.key, filter.value)}
					aria-label={`Remove filter: ${filter.label}`}
				>
					{filter.label}
					<span className='close-icon'>
						<CloseIcon />
					</span>
				</FilterChip>
			))}
			<ClearAllButton
				type='button'
				onClick={onClearAll}
				aria-label='Clear all filters'
			>
				Clear All
			</ClearAllButton>
		</ActiveFiltersContainer>
	);
};
